import { useId, useState } from 'react';
import { DELETION_REASON_MAX } from '@/domain';
import { strings } from '@/shared/strings';
import { ConfirmSheet } from '@/ui/primitives/ConfirmSheet';
import { modalStyles } from '@/ui/primitives/Modal';

export interface DeleteCaseDialogProps {
  jobOrderNo: string;
  /** What pressing the button does for this caller: delete now, or ask. */
  outcome: 'delete' | 'request';
  busy: boolean;
  /** The server's refusal, shown where the choice was made. */
  error: string | null;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

/**
 * Deletes a case, or asks for it to be deleted.
 *
 * The one line under the title says which, and so does the button, so nobody
 * presses it expecting the other: a manager who means to ask a colleague first
 * would otherwise delete the case, and reception who means to delete it would
 * be surprised to find it still on the floor.
 *
 * Since the request of 5 October 2026 (third), section 2, the dialog is the
 * confirmation and not a form in front of one: built on the shared
 * `ConfirmSheet`, with the question naming the case above the reason. A missing
 * reason keeps the sheet where it is, and so does the server's refusal.
 */
export function DeleteCaseDialog({
  jobOrderNo,
  outcome,
  busy,
  error,
  onCancel,
  onConfirm,
}: DeleteCaseDialogProps) {
  const reasonId = useId();
  const [reason, setReason] = useState('');
  const [missing, setMissing] = useState(false);

  function confirm(): boolean {
    if (reason.trim() === '') {
      setMissing(true);
      return false;
    }
    onConfirm(reason.trim());
    return true;
  }

  const shown = missing ? strings.deletion.reasonRequired : error;
  const label = outcome === 'delete' ? strings.deletion.delete : strings.deletion.request;

  return (
    <ConfirmSheet
      title={`${strings.deletion.title} · ${jobOrderNo}`}
      statement={
        outcome === 'delete'
          ? strings.confirm.deleteCase(jobOrderNo)
          : strings.confirm.askDeletion(jobOrderNo)
      }
      confirmLabel={label}
      cancelLabel={strings.deletion.cancel}
      closeLabel={strings.sheet.close}
      onConfirm={confirm}
      onCancel={onCancel}
      busy={busy}
    >
      <div className={modalStyles.field}>
        <span className={modalStyles.hint}>
          {outcome === 'delete' ? strings.deletion.leadDelete : strings.deletion.leadRequest}
        </span>
      </div>

      <div className={modalStyles.field}>
        <label className={modalStyles.label} htmlFor={reasonId}>
          {strings.deletion.reasonLabel}
        </label>
        <textarea
          id={reasonId}
          className={modalStyles.textarea}
          value={reason}
          maxLength={DELETION_REASON_MAX}
          placeholder={strings.deletion.reasonPlaceholder}
          data-autofocus
          onChange={(event) => {
            setReason(event.target.value);
            setMissing(false);
          }}
        />
      </div>

      {shown !== null && (
        <p className={modalStyles.error} role="alert">
          {shown}
        </p>
      )}
    </ConfirmSheet>
  );
}
